import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '@/utils/api';
import { useReintentoTrasSesion } from '@/utils/sesion';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Loader2, Save, Info, Lock, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import { formatFecha } from '@/components/SeguimientoAlumno';

// Límites de la caminata para el chequeo en vivo de la medición basal.
// Mantener en sync con backend/pruebas_funcionales.py (C6M_FC_REPOSO_MAX,
// C6M_SAT_MIN, C6M_DESATURACION_PUNTOS).
const C6M_FC_REPOSO_MAX = 120;
const C6M_SAT_MIN = 89;
const C6M_DESATURACION_PUNTOS = 4;

/** 'M', 'F' o null, igual que _sexo_norm del backend. */
function sexoNorm(sexo) {
  if (!sexo) return null;
  const s = String(sexo).trim().toLowerCase();
  if (s.startsWith('m') && !s.includes('muj')) return 'M';
  if (s.startsWith('f') || s.includes('muj')) return 'F';
  return null;
}

/** Botón que se marca y desmarca, para las preguntas de sí o no. */
function Opcion({ activa, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-md text-sm border transition text-left ${
        activa
          ? 'bg-slate-800 text-white border-slate-800'
          : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Bloque de una prueba. Se activa con un interruptor; si está bloqueada por el
 * escenario del alumno, no se puede activar y se explica por qué.
 */
function BloquePrueba({ titulo, protocolo, activa, onToggle, bloqueada, motivoBloqueo, children }) {
  return (
    <Card className={bloqueada ? 'border-slate-200 bg-slate-50/60' : 'border-slate-200'}>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-base font-['Manrope'] text-slate-700 flex items-center gap-2">
              {bloqueada && <Lock className="h-4 w-4 text-slate-400 shrink-0" />}
              {titulo}
            </CardTitle>
            {protocolo && <CardDescription className="mt-1">{protocolo}</CardDescription>}
          </div>
          {bloqueada ? (
            <span className="text-xs px-2.5 py-1 rounded-full border border-slate-200 bg-white text-slate-500 shrink-0">
              No disponible
            </span>
          ) : (
            <Button
              type="button"
              size="sm"
              variant={activa ? 'default' : 'outline'}
              onClick={() => onToggle(!activa)}
              className="shrink-0"
            >
              {activa ? 'Se hizo' : 'Marcar como hecha'}
            </Button>
          )}
        </div>
      </CardHeader>
      {bloqueada ? (
        <CardContent>
          <p className="text-sm text-slate-500">{motivoBloqueo}</p>
        </CardContent>
      ) : (
        activa && <CardContent className="space-y-4">{children}</CardContent>
      )}
    </Card>
  );
}

/** Campo numérico corto y etiquetado. */
function Campo({ id, label, sufijo, ...props }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs text-slate-600">
        {label} {sufijo && <span className="text-slate-400">({sufijo})</span>}
      </Label>
      <Input id={id} type="number" {...props} />
    </div>
  );
}

const VACIO = {
  c6m_realizada: false,
  c6m_distancia_m: '',
  c6m_pasillo_m: 30,
  c6m_fc_basal: '', c6m_fc_final: '', c6m_fc_recuperacion: '',
  c6m_sat_basal: '', c6m_sat_final: '',
  c6m_borg_disnea_basal: '', c6m_borg_disnea_final: '',
  c6m_borg_fatiga_basal: '', c6m_borg_fatiga_final: '',
  c6m_detenido_antes: false,
  c6m_motivo_detencion: '',
  c6m_angina_iam_reciente: false,
  c6m_imposibilidad_caminar: false,
  astrand_realizada: false,
  astrand_fc_final: '',
  sp_realizada: false,
  sp_repeticiones: '',
  hg_realizada: false,
  hg_derecha_kg: '', hg_izquierda_kg: '',
  observaciones: '',
};

export default function MedicionFuncionalPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ctx, setCtx] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [f, setF] = useState(VACIO);
  // Peso, talla y edad del día. Arrancan con lo de la última entrevista y se
  // pueden corregir: las ecuaciones dependen de ellos.
  const [base, setBase] = useState({ peso_kg: '', talla_cm: '', edad: '' });

  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
  const setB = (k, v) => setBase((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    let cancelado = false;
    api.getContextoMediciones(id)
      .then((c) => {
        if (cancelado) return;
        setCtx(c);
        const d = c?.datos || {};
        setBase({ peso_kg: d.peso_kg ?? '', talla_cm: d.talla_cm ?? '', edad: d.edad ?? '' });
      })
      .catch((e) => { if (!cancelado) toast.error(e?.message || 'No se pudo cargar el alumno'); })
      .finally(() => { if (!cancelado) setCargando(false); });
    return () => { cancelado = true; };
  }, [id]);

  const habilitacion = ctx?.habilitacion;
  const todoBloqueado = ctx && !habilitacion?.todas?.enabled;
  const submaximoBloqueado = ctx && !habilitacion?.submaximas?.enabled;
  const motivoBloqueo = habilitacion?.submaximas?.reason
    || 'El escenario vigente del alumno no habilita esfuerzo submáximo sostenido.';

  const datos = ctx?.datos || {};
  const sexo = datos.sexo;
  const sexoN = sexoNorm(sexo);
  const alturaEscalon = sexoN === 'M' ? '40 cm' : sexoN === 'F' ? '33 cm'
    : '33 cm (mujeres) o 40 cm (hombres)';

  // Chequeo en vivo de lo que se carga antes de caminar.
  const nv = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
  const avisosBasal = [];
  if (nv(f.c6m_fc_basal) !== null && nv(f.c6m_fc_basal) > C6M_FC_REPOSO_MAX) {
    avisosBasal.push(`FC basal de ${f.c6m_fc_basal} lpm, por encima de ${C6M_FC_REPOSO_MAX}: contraindicación relativa.`);
  }
  if (nv(f.c6m_sat_basal) !== null && nv(f.c6m_sat_basal) < C6M_SAT_MIN) {
    avisosBasal.push(`Saturación basal de ${f.c6m_sat_basal} %, por debajo de ${C6M_SAT_MIN}: contraindicación relativa.`);
  }
  if (f.c6m_angina_iam_reciente || f.c6m_imposibilidad_caminar) {
    avisosBasal.push('Marcaste una contraindicación absoluta para la caminata.');
  }
  const desaturacion = nv(f.c6m_sat_basal) !== null && nv(f.c6m_sat_final) !== null
    && nv(f.c6m_sat_basal) - nv(f.c6m_sat_final) >= C6M_DESATURACION_PUNTOS;

  const guardar = async () => {
    if (!f.c6m_realizada && !f.astrand_realizada && !f.sp_realizada && !f.hg_realizada) {
      toast.error('Marcá al menos una prueba como realizada');
      return;
    }
    const n = nv;
    const payload = {
      peso_kg: n(base.peso_kg),
      talla_cm: n(base.talla_cm),
      edad: n(base.edad),
      c6m_realizada: f.c6m_realizada,
      c6m_distancia_m: n(f.c6m_distancia_m),
      c6m_pasillo_m: n(f.c6m_pasillo_m) ?? 30,
      c6m_fc_basal: n(f.c6m_fc_basal),
      c6m_fc_final: n(f.c6m_fc_final),
      c6m_fc_recuperacion: n(f.c6m_fc_recuperacion),
      c6m_sat_basal: n(f.c6m_sat_basal),
      c6m_sat_final: n(f.c6m_sat_final),
      c6m_borg_disnea_basal: n(f.c6m_borg_disnea_basal),
      c6m_borg_disnea_final: n(f.c6m_borg_disnea_final),
      c6m_borg_fatiga_basal: n(f.c6m_borg_fatiga_basal),
      c6m_borg_fatiga_final: n(f.c6m_borg_fatiga_final),
      c6m_detenido_antes: f.c6m_detenido_antes,
      c6m_motivo_detencion: f.c6m_motivo_detencion || null,
      c6m_angina_iam_reciente: f.c6m_angina_iam_reciente,
      c6m_imposibilidad_caminar: f.c6m_imposibilidad_caminar,
      astrand_realizada: f.astrand_realizada,
      astrand_fc_final: n(f.astrand_fc_final),
      sp_realizada: f.sp_realizada,
      sp_repeticiones: n(f.sp_repeticiones),
      hg_realizada: f.hg_realizada,
      hg_derecha_kg: n(f.hg_derecha_kg),
      hg_izquierda_kg: n(f.hg_izquierda_kg),
      observaciones: f.observaciones || null,
    };
    try {
      setGuardando(true);
      await api.createMedicion(id, payload);
      toast.success('Mediciones registradas');
      navigate(`/alumnos/${id}`);
    } catch (e) {
      if (e?.status === 401) {
        marcarPendiente();
        toast.info('Tu sesión se venció. Ingresá de nuevo: lo cargado no se perdió y se guarda solo.');
        return;
      }
      toast.error(e?.message || 'No se pudieron guardar');
    } finally {
      setGuardando(false);
    }
  };

  const marcarPendiente = useReintentoTrasSesion(() => guardar());

  if (cargando) {
    return (
      <Layout>
        <div className="py-20 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      </Layout>
    );
  }

  const contra = ctx?.contraindicaciones_caminata;

  return (
    <Layout>
      <div className="space-y-6 max-w-3xl">
        <div>
          <Link
            to={`/alumnos/${id}`}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-3"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver a la ficha
          </Link>
          <h1 className="text-2xl font-bold text-gray-800 font-['Inter']">
            Pruebas funcionales {ctx?.alumno ? `— ${ctx.alumno.nombre}` : ''}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Marcá solo las pruebas que hiciste.
          </p>
        </div>

        {todoBloqueado ? (
          <Card className="border-slate-200 bg-slate-50/60">
            <CardContent className="py-6 space-y-4">
              <p className="text-sm text-slate-700 flex items-start gap-2">
                <Lock className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
                {habilitacion?.todas?.reason}
              </p>
              {!ctx?.fecha_ultima_entrevista && (
                <Button onClick={() => navigate(`/new-evaluation?alumno=${id}`)} className="gap-2">
                  <ClipboardList className="h-4 w-4" />
                  Entrevistar
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
        <>
        {/* Datos para los cálculos: vienen de la última entrevista y se pueden
            corregir con los del día */}
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 space-y-3">
          <p className="text-sm text-slate-600">
            Datos para los cálculos. Vienen de la entrevista del{' '}
            {formatFecha(ctx?.fecha_ultima_entrevista)}
            {datos.edad_origen === 'fecha_nacimiento' && ' (la edad, de la fecha de nacimiento)'}.
            Si cambiaron, corregilos con los de hoy.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Campo id="peso" label="Peso" sufijo="kg" step="0.1" min="20" max="300"
              value={base.peso_kg} onChange={(e) => setB('peso_kg', e.target.value)} />
            <Campo id="talla" label="Talla" sufijo="cm" min="100" max="230"
              value={base.talla_cm} onChange={(e) => setB('talla_cm', e.target.value)} />
            <Campo id="edad" label="Edad" sufijo="años" min="10" max="110"
              value={base.edad} onChange={(e) => setB('edad', e.target.value)} />
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Sexo</Label>
              <p className="h-10 flex items-center text-sm text-slate-700">{sexo || 'sin dato'}</p>
            </div>
          </div>
        </div>

        {/* Contraindicaciones de la caminata */}
        {(contra?.hay_absolutas || contra?.hay_relativas) && (
          <div className={`rounded-lg border px-4 py-3 ${
            contra.hay_absolutas
              ? 'border-red-200 bg-red-50 text-red-900'
              : 'border-amber-200 bg-amber-50 text-amber-900'
          }`}>
            <p className="font-semibold flex items-center gap-2">
              <Info className="h-4 w-4 shrink-0" />
              {contra.hay_absolutas
                ? 'Contraindicaciones para la caminata de 6 minutos'
                : 'Contraindicaciones relativas para la caminata de 6 minutos'}
            </p>
            <ul className="mt-2 space-y-1 text-sm list-disc list-inside">
              {[...contra.absolutas, ...contra.relativas].map((t, i) => <li key={i}>{t}</li>)}
            </ul>
            <p className="text-xs mt-2 opacity-80">
              Surgen de los datos de la entrevista del {formatFecha(ctx?.fecha_ultima_entrevista)}.
              Conviene volver a tomar FC y presión hoy. La decisión de realizar o no
              la prueba es tuya.
            </p>
          </div>
        )}

        {/* Caminata de 6 minutos */}
        <BloquePrueba
          titulo="Caminata de 6 minutos"
          protocolo="Pasillo recto de 30 m marcado cada 3 m, con conos en los extremos. Norma ATS."
          activa={f.c6m_realizada}
          onToggle={(v) => set('c6m_realizada', v)}
          bloqueada={submaximoBloqueado}
          motivoBloqueo={motivoBloqueo}
        >
          <div className="space-y-2">
            <p className="text-sm text-slate-600">Antes de empezar, confirmá:</p>
            <div className="flex flex-wrap gap-2">
              <Opcion activa={f.c6m_angina_iam_reciente}
                onClick={() => set('c6m_angina_iam_reciente', !f.c6m_angina_iam_reciente)}>
                Angina inestable o infarto en el último mes
              </Opcion>
              <Opcion activa={f.c6m_imposibilidad_caminar}
                onClick={() => set('c6m_imposibilidad_caminar', !f.c6m_imposibilidad_caminar)}>
                No puede caminar por un evento agudo (esguince, herida, fractura)
              </Opcion>
            </div>
            {datos.cardiopatia_declarada && (
              <p className="text-xs text-slate-500">
                En la entrevista declaró enfermedad cardíaca: preguntá si tuvo algún
                evento en el último mes.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Campo id="dist" label="Distancia" sufijo="m" min="0" value={f.c6m_distancia_m}
              onChange={(e) => set('c6m_distancia_m', e.target.value)} />
            <Campo id="pasillo" label="Pasillo" sufijo="m" min="1" max="100" value={f.c6m_pasillo_m}
              onChange={(e) => set('c6m_pasillo_m', e.target.value)} />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-100">
                  <th className="py-2 pr-3 font-medium">&nbsp;</th>
                  <th className="py-2 pr-3 font-medium">Basal</th>
                  <th className="py-2 pr-3 font-medium">Final</th>
                  <th className="py-2 font-medium">Recuperación</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="py-2 pr-3 text-slate-600">FC (lpm)</td>
                  <td className="py-2 pr-3"><Input type="number" min="30" max="250" value={f.c6m_fc_basal}
                    onChange={(e) => set('c6m_fc_basal', e.target.value)} /></td>
                  <td className="py-2 pr-3"><Input type="number" min="30" max="250" value={f.c6m_fc_final}
                    onChange={(e) => set('c6m_fc_final', e.target.value)} /></td>
                  <td className="py-2"><Input type="number" min="30" max="250" value={f.c6m_fc_recuperacion}
                    onChange={(e) => set('c6m_fc_recuperacion', e.target.value)} /></td>
                </tr>
                <tr>
                  <td className="py-2 pr-3 text-slate-600">Saturación (%)</td>
                  <td className="py-2 pr-3"><Input type="number" min="50" max="100" value={f.c6m_sat_basal}
                    onChange={(e) => set('c6m_sat_basal', e.target.value)} /></td>
                  <td className="py-2 pr-3"><Input type="number" min="50" max="100" value={f.c6m_sat_final}
                    onChange={(e) => set('c6m_sat_final', e.target.value)} /></td>
                  <td className="py-2 text-slate-300 text-center">—</td>
                </tr>
                <tr>
                  <td className="py-2 pr-3 text-slate-600">Borg disnea</td>
                  <td className="py-2 pr-3"><Input type="number" step="0.5" min="0" max="10" value={f.c6m_borg_disnea_basal}
                    onChange={(e) => set('c6m_borg_disnea_basal', e.target.value)} /></td>
                  <td className="py-2 pr-3"><Input type="number" step="0.5" min="0" max="10" value={f.c6m_borg_disnea_final}
                    onChange={(e) => set('c6m_borg_disnea_final', e.target.value)} /></td>
                  <td className="py-2 text-slate-300 text-center">—</td>
                </tr>
                <tr>
                  <td className="py-2 pr-3 text-slate-600">Borg fatiga</td>
                  <td className="py-2 pr-3"><Input type="number" step="0.5" min="0" max="10" value={f.c6m_borg_fatiga_basal}
                    onChange={(e) => set('c6m_borg_fatiga_basal', e.target.value)} /></td>
                  <td className="py-2 pr-3"><Input type="number" step="0.5" min="0" max="10" value={f.c6m_borg_fatiga_final}
                    onChange={(e) => set('c6m_borg_fatiga_final', e.target.value)} /></td>
                  <td className="py-2 text-slate-300 text-center">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          {avisosBasal.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <ul className="space-y-1 list-disc list-inside">
                {avisosBasal.map((t) => <li key={t}>{t}</li>)}
              </ul>
              <p className="text-xs mt-1 opacity-80">
                Queda registrado en la medición. La decisión de hacer la prueba es tuya.
              </p>
            </div>
          )}
          {desaturacion && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              La saturación bajó {nv(f.c6m_sat_basal) - nv(f.c6m_sat_final)} puntos: se
              considera desaturación significativa y queda anotado en el informe.
            </p>
          )}

          <div className="space-y-2">
            <Opcion activa={f.c6m_detenido_antes}
              onClick={() => set('c6m_detenido_antes', !f.c6m_detenido_antes)}>
              Se detuvo antes de los 6 minutos
            </Opcion>
            {f.c6m_detenido_antes && (
              <Input
                placeholder="Motivo de la detención"
                value={f.c6m_motivo_detencion}
                onChange={(e) => set('c6m_motivo_detencion', e.target.value)}
              />
            )}
          </div>
        </BloquePrueba>

        {/* Escalón de Åstrand-Ryhming */}
        <BloquePrueba
          titulo="Escalón de Åstrand-Ryhming"
          protocolo={`Escalón de ${alturaEscalon}, 22,5 ciclos por minuto marcados con metrónomo, durante 5 minutos.`}
          activa={f.astrand_realizada}
          onToggle={(v) => set('astrand_realizada', v)}
          bloqueada={submaximoBloqueado}
          motivoBloqueo={motivoBloqueo}
        >
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Campo id="astfc" label="FC al final del minuto 5" sufijo="lpm"
              value={f.astrand_fc_final}
              onChange={(e) => set('astrand_fc_final', e.target.value)} />
          </div>
          {datos.betabloqueante ? (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              En la entrevista declaró que toma betabloqueante: la frecuencia está frenada
              y el VO₂ máximo va a salir sobreestimado. Queda anotado en el informe; si
              tenés espacio, la caminata de 6 minutos no depende de la FC.
            </p>
          ) : (
            <p className="text-xs text-slate-500">
              El VO₂ máximo se estima sobre esa frecuencia y se corrige por edad. Es una
              estimación indirecta: con betabloqueantes la frecuencia está frenada y el
              resultado queda sobreestimado.
            </p>
          )}
          {!sexoN && (
            <p className="text-xs text-slate-500">
              El nomograma solo tiene escalas para sexo masculino y femenino: la FC se
              guarda, pero el VO₂ máximo no se calcula.
            </p>
          )}
        </BloquePrueba>

        {/* Sentarse-pararse */}
        <BloquePrueba
          titulo="Sentarse-pararse en 30 segundos"
          protocolo="Silla sin apoyabrazos de unos 43 cm, contra la pared. Sentado en el medio del asiento, espalda recta, pies apoyados, brazos cruzados sobre el pecho. Una o dos repeticiones de ensayo antes. Si al terminar los 30 s está a más de mitad del recorrido, cuenta como completa."
          activa={f.sp_realizada}
          onToggle={(v) => set('sp_realizada', v)}
        >
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Campo id="sprep" label="Repeticiones completas" min="0" max="80" value={f.sp_repeticiones}
              onChange={(e) => set('sp_repeticiones', e.target.value)} />
          </div>
        </BloquePrueba>

        {/* Handgrip */}
        <BloquePrueba
          titulo="Dinamometría de mano"
          protocolo="Protocolo Southampton: sentado con respaldo, antebrazo apoyado, muñeca neutra. Se registra el mejor intento de cada mano."
          activa={f.hg_realizada}
          onToggle={(v) => set('hg_realizada', v)}
        >
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Campo id="hgd" label="Mano derecha" sufijo="kg" step="0.1" min="0" max="120"
              value={f.hg_derecha_kg} onChange={(e) => set('hg_derecha_kg', e.target.value)} />
            <Campo id="hgi" label="Mano izquierda" sufijo="kg" step="0.1" min="0" max="120"
              value={f.hg_izquierda_kg} onChange={(e) => set('hg_izquierda_kg', e.target.value)} />
          </div>
        </BloquePrueba>

        {/* Observaciones */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-['Manrope'] text-slate-700">
              Observaciones
            </CardTitle>
          </CardHeader>
          <CardContent>
            <textarea
              rows={3}
              value={f.observaciones}
              onChange={(e) => set('observaciones', e.target.value)}
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
              placeholder="Opcional"
            />
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2 pb-4">
          <Button variant="outline" onClick={() => navigate(`/alumnos/${id}`)} disabled={guardando}>
            Cancelar
          </Button>
          <Button onClick={guardar} disabled={guardando} className="gap-2">
            {guardando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Guardar mediciones
          </Button>
        </div>
        </>
        )}
      </div>
    </Layout>
  );
}
